/* eslint-disable typescript-extended-rules/file-naming-convention */
import { TypeKinship } from '../../../shared/enums/type-kinship.enum';

export type BeneficiaryReconciliationField =
	| 'ACTIVE_MISSING_IN_DB'
	| 'CARD_MISMATCH'
	| 'DUPLICATE_IN_FILE'
	| 'INACTIVE_MISSING_IN_FILE'
	| 'ORGANIZATION_MISMATCH'
	| 'PLAN_MISMATCH'
	| 'TITULARITY_MISMATCH';

export enum BeneficiaryReconciliationItemStatus {
	APPLIED = 'APPLIED',
	FAILED = 'FAILED',
	PENDING = 'PENDING',
}

export enum BeneficiaryReconciliationReviewState {
	MANUAL_REVIEW_REQUIRED = 'MANUAL_REVIEW_REQUIRED',
	READY_TO_APPLY = 'READY_TO_APPLY',
}

export enum BeneficiaryReconciliationItemStatusFilter {
	ALL = '',
	APPLIED = 'APPLIED',
	FAILED = 'FAILED',
	IGNORED = 'IGNORED',
	MANUAL_REVIEW_REQUIRED = 'MANUAL_REVIEW_REQUIRED',
	PENDING = 'PENDING',
	READY_TO_APPLY = 'READY_TO_APPLY',
}

export type BeneficiaryReconciliationSessionStatus = 'COMPLETED' | 'FAILED';
export type BeneficiaryReconciliationSessionProgressStatus =
	| 'COMPARED_ONLY'
	| 'COMPLETED'
	| 'IN_PROGRESS';

export type BeneficiaryReconciliationApplyAction =
	| 'CREATE_BENEFICIARY'
	| 'IGNORE_ITEM'
	| 'INACTIVATE_BENEFICIARY'
	| 'LINK_EXISTING_BENEFICIARY'
	| 'REACTIVATE_BENEFICIARY'
	| 'SELECT_DUPLICATE_ROW'
	| 'TRANSFER_ORGANIZATION'
	| 'UPDATE_CARD'
	| 'UPDATE_PLAN'
	| 'UPDATE_PLAN_AND_CARD'
	| 'UPDATE_TITULARITY';

export type BeneficiaryReconciliationProcessingStatus =
	| 'failed'
	| 'finished'
	| 'processing';

export type BeneficiaryReconciliationProcessingType = 'APPLY' | 'COMPARE';

export type BeneficiaryReconciliationDraftAction =
	| 'CREATE_SUB'
	| 'RESOLVE_PREFLIGHT_ROW'
	| 'RESUME_PREFLIGHT_ROW'
	| 'TREAT_AS_MATRIX'
	| BeneficiaryReconciliationApplyAction;

export interface BeneficiaryReconciliationSummary {
	ACTIVE_MISSING_IN_DB: number;
	CARD_MISMATCH: number;
	DUPLICATE_IN_FILE: number;
	INACTIVE_MISSING_IN_FILE: number;
	ORGANIZATION_MISMATCH: number;
	PLAN_MISMATCH: number;
	TITULARITY_MISMATCH: number;
}

export type GenericBeneficiaryReconciliationWalletKind = 'DENTAL' | 'HEALTH';
export type BeneficiaryReconciliationBenefitVariant =
	| 'CONGENER'
	| 'CONJUGATED'
	| 'NORMAL';
export type BeneficiaryReconciliationBenefitVariantLabel =
	| 'Congênere'
	| 'Conjugado'
	| 'Normal';
export type BeneficiaryReconciliationWalletType =
	| 'CONGENER'
	| 'CONJUGATED'
	| 'DEFAULT';

export interface GenericBeneficiaryReconciliationPreflightIssue {
	code: string;
}

export type GenericBeneficiaryReconciliationPreflightRowStatus =
	| 'ELIGIBLE'
	| 'ON_HOLD'
	| 'PENDING';

export type GenericBeneficiaryReconciliationPreflightListStatus =
	| 'ALL'
	| 'APPLIED'
	| 'IGNORED'
	| GenericBeneficiaryReconciliationPreflightRowStatus;

export interface GenericBeneficiaryReconciliationUnknownCnpjAlert {
	cnpj: string;
	resolvedAt?: string;
	resolvedBy?: string;
	rowIds: string[];
	status: 'PENDING' | 'TREATED_AS_MATRIX';
	treatedAsOrganizationId?: string;
}

export type GenericBeneficiaryReconciliationImportModel =
	| 'ASSISTED_MAPPING'
	| 'BRADESCO_PROVIDER'
	| 'SANUS_STANDARD';

export interface GenericBeneficiaryReconciliationPreflightSummary {
	counts: {
		eligible: number;
		onHold: number;
		pending: number;
		total: number;
	};
	files: {
		fileName: string;
		walletKind: GenericBeneficiaryReconciliationWalletKind;
	}[];
	importModel?: GenericBeneficiaryReconciliationImportModel;
	matrizOrganizationId: string;
	organizationAlerts: {
		dentalPlanNotRegistered: boolean;
		missingSubs: {
			cnpj: string | null;
			name: string | null;
			organizationId: string;
		}[];
		unknownCnpjs: GenericBeneficiaryReconciliationUnknownCnpjAlert[];
	};
	pendencyCounts?: {
		uncomparedLines: number;
	};
	phase: 'PREFLIGHT';
	rows?: {
		id: string;
		issues: GenericBeneficiaryReconciliationPreflightIssue[];
		status: GenericBeneficiaryReconciliationPreflightRowStatus;
		warnings: GenericBeneficiaryReconciliationPreflightIssue[];
	}[];
	selectedOrganizationId: string;
	version: 1;
}

export enum BeneficiaryReconciliationMappingCanonicalField {
	BENEFICIARY_CPF = 'beneficiaryCpf',
	BENEFICIARY_NAME = 'beneficiaryName',
	BIRTH_DAY = 'birthDay',
	CARRIER = 'carrier',
	CONGENER_CARD_NUMBER = 'congenerCardNumber',
	CONGENER_PLAN_NAME = 'congenerPlanName',
	CONJUGATED_CARD_NUMBER = 'conjugatedCardNumber',
	CONJUGATED_PLAN_NAME = 'conjugatedPlanName',
	DENTAL_CARD_NUMBER = 'dentalCardNumber',
	DENTAL_PLAN_NAME = 'dentalPlanName',
	HEALTH_CARD_NUMBER = 'healthCardNumber',
	HEALTH_PLAN_NAME = 'healthPlanName',
	HOLDER_CPF = 'holderCpf',
	PARENTESCO = 'parentesco',
	SOURCE_ORGANIZATION_CNPJ = 'sourceOrganizationCnpj',
	TITULARITY = 'titularity',
}

export enum BeneficiaryReconciliationMappingFieldState {
	COLUMN = 'COLUMN',
	MISSING_FROM_SOURCE = 'MISSING_FROM_SOURCE',
	NOT_APPLICABLE = 'NOT_APPLICABLE',
	PENDING = 'PENDING',
}

export enum BeneficiaryReconciliationMappingSourceUsage {
	IGNORE = 'IGNORE',
	USE = 'USE',
}

export enum BeneficiaryReconciliationMappingBenefitKind {
	CONJUGATED = 'CONJUGATED',
	DENTAL = 'DENTAL',
	HEALTH = 'HEALTH',
	HEALTH_DENTAL = 'HEALTH_DENTAL',
}

export enum BeneficiaryReconciliationMappingTitularityKind {
	DEPENDENTS = 'DEPENDENTS',
	HOLDERS = 'HOLDERS',
	HOLDERS_DEPENDENTS = 'HOLDERS_DEPENDENTS',
}

export interface BeneficiaryReconciliationMappingColumn {
	index: number;
	normalizedName: string;
	sampleValues: string[];
	sourceName: string;
}

export interface BeneficiaryReconciliationMappingSuggestion {
	column: string | null;
	confidence: number;
	field: BeneficiaryReconciliationMappingCanonicalField;
	reason: string;
}

export interface BeneficiaryReconciliationMappingSourceAnalysis {
	columns: BeneficiaryReconciliationMappingColumn[];
	fileIndex: number;
	fileName: string;
	rowCount: number;
	sampleRows: Record<string, string | null>[];
	sheetName: string;
	sourceId: string;
	suggestions?: BeneficiaryReconciliationMappingSuggestion[];
}

export interface BeneficiaryReconciliationMappingFileSummary {
	fileIndex: number;
	fileName: string;
	sheetCount: number;
	size: number;
}

export interface BeneficiaryReconciliationMappingAnalysisResponse {
	files: BeneficiaryReconciliationMappingFileSummary[];
	sources: BeneficiaryReconciliationMappingSourceAnalysis[];
}

export interface BeneficiaryReconciliationMappingFieldSelection {
	column?: string;
	state: BeneficiaryReconciliationMappingFieldState;
}

export interface BeneficiaryReconciliationMappingSourceConfig {
	benefitKind: BeneficiaryReconciliationMappingBenefitKind;
	fields: Partial<
		Record<
			BeneficiaryReconciliationMappingCanonicalField,
			BeneficiaryReconciliationMappingFieldSelection
		>
	>;
	fileIndex: number;
	fileName: string;
	sheetName: string;
	sourceId: string;
	titularityKind: BeneficiaryReconciliationMappingTitularityKind;
	usage: BeneficiaryReconciliationMappingSourceUsage;
}

export interface BeneficiaryReconciliationMappingConfirmation {
	sources: BeneficiaryReconciliationMappingSourceConfig[];
}

export interface GenericBeneficiaryReconciliationPreflightRowData {
	beneficiaryCpf?: string | null;
	beneficiaryName?: string | null;
	carrier?: string | null;
	source?: {
		fileName: string;
		rowNumber: number;
		sheetName: string;
	};
	sourceMetadata?: {
		normalizedSubinvoiceNumber?: string | null;
		numeroSubfatura?: string | null;
		subinvoiceNotFound?: boolean;
	};
	sourceOrganizationCnpj?: string | null;
	walletKind?: GenericBeneficiaryReconciliationWalletKind;
}

export interface GenericBeneficiaryReconciliationPreflightRow {
	id: string;
	data?: GenericBeneficiaryReconciliationPreflightRowData;
	issues: GenericBeneficiaryReconciliationPreflightIssue[];
	lastAction?: string;
	status: GenericBeneficiaryReconciliationPreflightRowStatus;
	updatedAt?: string;
	updatedBy?: string;
	warnings: GenericBeneficiaryReconciliationPreflightIssue[];
}

export interface GenericBeneficiaryReconciliationPreflightPendenciesResponse {
	items: GenericBeneficiaryReconciliationPreflightRow[];
	limit: number;
	page: number;
	total: number;
	totalPages: number;
}

export interface GenericBeneficiaryReconciliationCreateSubPrefill {
	cnpj: string;
	isSub: true;
	matrizOrganizationId: string;
}
export interface GenericBeneficiaryReconciliationPreflightImportResult {
	error: string | null;
	rowId: string;
	rowNumber: number;
	status: 'ERROR' | 'SUCCESS';
}

export interface GenericBeneficiaryReconciliationPreflightImportResponse {
	failed: number;
	processed: number;
	results: GenericBeneficiaryReconciliationPreflightImportResult[];
	succeeded: number;
}

export type GenericBeneficiaryReconciliationPreflightExportKind =
	| 'UNCOMPARED_LINES'
	| 'UNIDENTIFIED_ORGANIZATIONS';

export interface GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup {
	appliedCount?: number;
	canCreateSub: boolean;
	canHold: boolean;
	canIgnore: boolean;
	canResume: boolean;
	canTreatAsMatrix: boolean;
	cnpj: string | null;
	ignoredCount?: number;
	issueCodes: string[];
	key: string;
	normalizedSubinvoiceNumber?: string | null;
	onHoldCount: number;
	onHoldRowIds: string[];
	pendingCount: number;
	pendingRowIds: string[];
	rowCount: number;
	rowIds: string[];
	status: GenericBeneficiaryReconciliationPreflightRowStatus;
	subinvoiceNumber?: string | null;
}

export interface GenericBeneficiaryReconciliationUnidentifiedOrganizationGroupsResponse {
	items: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup[];
	total: number;
}

export interface BeneficiaryReconciliationCandidateMatch {
	active: boolean;
	beneficiaryCpf: string;
	beneficiaryId: string;
	beneficiaryName: string | null;
	carrier: string;
	holderCpf: string | null;
	inactivedAt: string | null;
	isDeleted: boolean;
	isSameMatrix: boolean;
	organizationCnpj: string | null;
	organizationId: string | null;
	organizationName: string | null;
	planName: string | null;
	retiredAt: string | null;
	typeKinship: TypeKinship;
}

export interface BeneficiaryReconciliationItemContext {
	[key: string]: unknown;
	actionLabel?: string | null;
	benefitVariant?: BeneficiaryReconciliationBenefitVariant;
	benefitVariantLabel?: BeneficiaryReconciliationBenefitVariantLabel;
	candidateMatches?: BeneficiaryReconciliationCandidateMatch[];
	createBlockedBecauseCpfExistsOutsideMatrix?: boolean;
	currentOrganizationCnpj?: string | null;
	currentOrganizationId?: string | null;
	currentOrganizationName?: string | null;
	dentalNotApplicableCandidate?: boolean;
	duplicateCount?: number;
	duplicateKey?: string;
	expectedOrganizationCnpj?: string | null;
	expectedOrganizationId?: string | null;
	expectedOrganizationName?: string | null;
	incompleteData?: boolean;
	manualReviewRequired?: boolean;
	missingFields?: string[];
	missingRequiredFields?: string[];
	organizationOffersDental?: boolean;
	reason?: string | null;
	resolution?: {
		action?: BeneficiaryReconciliationApplyAction;
		appliedAt?: string;
		appliedById?: string;
		appliedByName?: string;
		failedAt?: string;
		payload?: unknown;
	};
	resolutionError?: string | null;
	resolutionNote?: string | null;
	resolutionOptions?: BeneficiaryReconciliationApplyAction[];
	rowNumbers?: number[];
	sheetNames?: string[];
	sourceFiles?: string[];
	suggestedAction?: BeneficiaryReconciliationApplyAction | null;
	suggestedTargetValue?: BeneficiaryReconciliationApplyTargetValue | null;
	walletKind?: string | null;
	walletType?: BeneficiaryReconciliationWalletType;
	warnings?: string[];
}

export interface BeneficiaryReconciliationItem {
	id: string;
	appliedAt?: string | null;
	beneficiaryCpf: string;
	beneficiaryId?: string | null;
	beneficiaryName?: string | null;
	carrier?: string | null;
	comparisonKey: string;
	context?: BeneficiaryReconciliationItemContext | null;
	createdAt: string;
	dbValue?: Record<string, unknown> | null;
	errorMessage?: string | null;
	externalValue?: Record<string, unknown> | null;
	field: BeneficiaryReconciliationField;
	holderCpf?: string | null;
	organizationCnpj?: string | null;
	organizationId?: string | null;
	organizationName?: string | null;
	resolvable: boolean;
	rowNumber?: number | null;
	sessionId: string;
	sheetName?: string | null;
	sourceFileName?: string | null;
	status: BeneficiaryReconciliationItemStatus;
	suggestedAction?: BeneficiaryReconciliationApplyAction | null;
	updatedAt: string;
}

export interface BeneficiaryReconciliationSessionListItem {
	id: string;
	appliedCount?: number;
	createdAt: string;
	createdBy?: string | null;
	createdByName?: string | null;
	progressStatus?: BeneficiaryReconciliationSessionProgressStatus;
	status: BeneficiaryReconciliationSessionStatus;
	summary:
		| (BeneficiaryReconciliationSummary & {
				preflight?: GenericBeneficiaryReconciliationPreflightSummary;
		  })
		| null;
	total: number;
}

export interface BeneficiaryReconciliationItemsResponse {
	comparisonId: string;
	items: BeneficiaryReconciliationItem[];
	limit: number;
	page: number;
	summary: BeneficiaryReconciliationSummary;
	total: number;
	totalPages: number;
}

export interface BeneficiaryReconciliationSessionsResponse {
	limit: number;
	page: number;
	sessions: BeneficiaryReconciliationSessionListItem[];
	total: number;
	totalPages: number;
}

export interface BeneficiaryReconciliationApplyTargetValue {
	beneficiaryId?: string;
	birthDay?: string;
	cardNumber?: string;
	carrier?: string;
	cascadeDependents?: boolean;
	cnpj?: string;
	cpf?: string;
	dentalNotApplicable?: boolean;
	email?: string;
	holderCpf?: string;
	name?: string;
	organizationId?: string;
	planName?: string;
	resolutionMode?: string;
	rowIds?: string[];
	rowNumber?: number;
	sheetName?: string;
	sourceFileName?: string;
	typeKinship?: TypeKinship;
	uid?: string;
}

export interface BeneficiaryReconciliationApplyRequest {
	confirmSuggested?: boolean;
	fields?: BeneficiaryReconciliationField[];
	items?: {
		action: BeneficiaryReconciliationApplyAction;
		itemId: string;
		targetValue: BeneficiaryReconciliationApplyTargetValue;
	}[];
}

export interface BeneficiaryReconciliationProcessingStartResponse {
	processingId: string;
	status: 'processing';
}

export interface BeneficiaryReconciliationProcessingStatusResponse {
	data?: Record<string, unknown> | null;
	error?: string | null;
	status: BeneficiaryReconciliationProcessingStatus;
}

export interface BeneficiaryReconciliationProcessingRecord
	extends BeneficiaryReconciliationProcessingStatusResponse {
	finishedAt?: string;
	organizationId: string;
	processingId: string;
	sessionId: string;
	startedAt: string;
	type: BeneficiaryReconciliationProcessingType;
	userId: string;
}

export type BeneficiaryReconciliationActiveProcessingResponse =
	BeneficiaryReconciliationProcessingRecord | null;

export interface BeneficiaryReconciliationDraftPendingChange {
	action: BeneficiaryReconciliationDraftAction;
	actionLabel?: string;
	field?: BeneficiaryReconciliationField;
	itemId: string;
	preflightKind?: GenericBeneficiaryReconciliationPreflightExportKind;
	targetValue: BeneficiaryReconciliationApplyTargetValue;
}

export interface BeneficiaryReconciliationDraftActiveApplyProcessing {
	fields?: BeneficiaryReconciliationField[];
	itemIds?: string[];
	processingId: string;
}

export interface BeneficiaryReconciliationDraft {
	[key: string]: unknown;
	activeApplyProcessing?: BeneficiaryReconciliationDraftActiveApplyProcessing;
	pendingChanges?: BeneficiaryReconciliationDraftPendingChange[];
}

export interface BeneficiaryReconciliationApplyResultItem {
	action: BeneficiaryReconciliationApplyAction;
	beneficiaryCpf: string;
	beneficiaryName?: string | null;
	carrier?: string | null;
	errorMessage?: string;
	field: BeneficiaryReconciliationField;
	itemId: string;
	status: BeneficiaryReconciliationItemStatus;
	targetValue: BeneficiaryReconciliationApplyTargetValue | null;
}

export interface BeneficiaryReconciliationApplyResponse {
	applied: number;
	comparisonId: string;
	failed: number;
	items: BeneficiaryReconciliationApplyResultItem[];
	processed: number;
	reportUrl: string | null;
}

export interface BeneficiaryReconciliationExportResponse {
	url: string;
}

export type BeneficiaryReconciliationComplementItemStatus =
	| 'COMPLEMENTED'
	| 'CREATED'
	| 'FAILED'
	| 'SKIPPED'
	| 'STILL_INCOMPLETE';

export interface BeneficiaryReconciliationComplementResultItem {
	beneficiaryCpf: string;
	beneficiaryName: string | null;
	itemId: string;
	reason?: string;
	status: BeneficiaryReconciliationComplementItemStatus;
}

export interface BeneficiaryReconciliationComplementResponse {
	comparisonId: string;
	complemented: number;
	created: number;
	failed: number;
	items: BeneficiaryReconciliationComplementResultItem[];
	skipped: number;
	stillIncomplete: number;
}

export interface BeneficiaryReconciliationDuplicateReturnError {
	itemId?: string | null;
	reason: string;
	rowNumber?: number | null;
}

export interface BeneficiaryReconciliationDuplicateReturnResponse {
	comparisonId: string;
	errors: BeneficiaryReconciliationDuplicateReturnError[];
	ignored: number;
	resolved: number;
	skippedAlreadyResolved: number;
	totalRows: number;
}

export interface BeneficiaryReconciliationTitularityImportResult {
	beneficiaryCpf?: string | null;
	caseCode?: string | null;
	error?: string | null;
	itemId?: string | null;
	rowNumber: number;
	status: 'ERROR' | 'SUCCESS';
}

export interface BeneficiaryReconciliationTitularityImportResponse {
	failed: number;
	processed: number;
	results: BeneficiaryReconciliationTitularityImportResult[];
	succeeded: number;
}

export const EMPTY_BENEFICIARY_RECONCILIATION_SUMMARY: BeneficiaryReconciliationSummary =
	{
		ACTIVE_MISSING_IN_DB: 0,
		CARD_MISMATCH: 0,
		DUPLICATE_IN_FILE: 0,
		INACTIVE_MISSING_IN_FILE: 0,
		ORGANIZATION_MISMATCH: 0,
		PLAN_MISMATCH: 0,
		TITULARITY_MISMATCH: 0,
	};
